import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.7 / §1.2.12: 22px, radius-full, 12px/500, soft tint + dark
// text. The ring-1 treatment was removed — a border on a 22px pill reads as
// visual noise next to the status dot and made every pill compete for weight.
//
// Ticket status and priority do NOT belong here as variants: they resolve
// through resolveStatus/resolvePriority (§1.2.3) so admin-configured statuses
// inherit a family instead of needing a new variant per value. Use
// StatusPill/PriorityPill from ./ticket-pills for those.
const badgeVariants = cva(
  'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2 text-xs font-medium',
  {
    variants: {
      variant: {
        default: 'bg-brand-50 text-brand-700',
        neutral: 'bg-surface-subtle text-ink-muted',
        info: 'bg-info-50 text-info-600',
        success: 'bg-success-50 text-success-700',
        warning: 'bg-warning-50 text-warning-700',
        danger: 'bg-danger-50 text-danger-600',
        accent: 'bg-accent-50 text-accent-600',
        outline: 'bg-white text-ink-muted ring-1 ring-inset ring-line-strong',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
